// Copyright (c) 2026 Celadon Development LLC, All rights reserved.
// Author Vasily Klyuev <vasily.klyuev@celadon.ae>
// Records that a customer opened the photo of a rejected book.
//
// The client asked how many customers actually look at the quality-fail
// pictures the inbound team takes, and nothing captured it. This reports the
// same opening twice over:
//
//   * as a Firebase/GA4 event, alongside the funnel events the site already
//     sends, and pushed to the GTM dataLayer so a tag can use it without a
//     code change;
//   * to our own service, which is what the COMS dashboard chart reads. Only
//     the server-side record can say "of the customers who HAD a rejection,
//     this many looked", because only it can be joined back to orders.
//
// Website only: the app has never been able to display these photos.
import * as firebase from 'firebase/app';

const EVENT_NAME = 'qf_image_view';

/**
 * Sends the event and the server-side record. Never throws and never returns
 * anything worth waiting for - a customer looking at a photo of their damaged
 * book must not see an error because a statistic failed to file.
 */
export const trackRejectionImageView = ({ orderDetailsId, rejectionReason, imageCount }) => {
  const customerId = localStorage.getItem('loggedID');
  const token = localStorage.getItem('token');

  try {
    firebase.analytics().logEvent(EVENT_NAME, {
      customer_id: customerId,
      order_details_id: orderDetailsId,
      rejection_reason: rejectionReason,
      image_count: imageCount,
    });
  } catch (error) {
    // Analytics being blocked - ad blockers do it routinely - is not a reason
    // to skip the server-side record below, so this is swallowed separately.
  }

  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: EVENT_NAME,
      orderDetailsId,
      rejectionReason,
      imageCount,
    });
  } catch (error) {
    // As above.
  }

  if (!customerId || !token || !orderDetailsId) {
    return;
  }

  // Deliberately fetch rather than the shared api() helper: api() treats a
  // "Not Authorized." reply as the session ending and pushes the user to the
  // login screen. Right for something the customer asked for, wrong for a
  // statistic - nobody should be thrown out of the page they are reading
  // because a tracking call came back stale.
  try {
    fetch('/Sellitback.svc/LogRejectionImageView', {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({
        CustomerId: parseInt(customerId, 10),
        Token: token,
        OrderDetailsId: orderDetailsId,
        Source: 'web',
        ImageCount: imageCount,
      }),
    }).catch(() => { });
  } catch (error) {
    // Same again: this is a side effect of looking at a picture, nothing more.
  }
};

export default trackRejectionImageView;
